import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Mic } from 'lucide-react'
import { SCENE_ROUTE } from './sceneRoute'
import { useCallSelector } from './selectors'
import { useDeviceStatus, useTelephony } from './TelephonyProvider'
import type { CallPhase } from './callMachine'
import type { TelephonyMicIssue } from './device'

/**
 * Badge d'état de la téléphonie v2 — remplace `ActiveCallBadge` (v1) quand le
 * flag est actif.
 *
 * POURQUOI il existe : `ActiveCallBadge` lit l'état de `twilioVoiceSimple`
 * (le Device v1). Or, flag v2 actif, le listener v1 s'arrête volontairement
 * (kill-switch — un seul Device par session) : le badge affichait donc
 * « Offline » en permanence alors que le poste v2 était bel et bien
 * enregistré et prenait des appels. Constat de la recette réelle du
 * 2026-07-22 — trompeur au point de faire douter d'un système qui marchait.
 *
 * Vue PURE : aucune logique, aucun appel réseau, aucun état local. Tout vient
 * de la machine à états et du statut du Device.
 *
 * `describe()` renvoie des CLÉS i18n, jamais des libellés : la fonction reste
 * pure et testable, et aucune lambda `t` locale ne vient masquer celle
 * d'i18next (piège relevé sur les lots i18n précédents).
 */

type Tone = 'live' | 'ready' | 'warn' | 'off'

/** Libellé de la pastille par cause micro (issue #190) — clés écrites en entier. */
const MIC_BADGE_KEYS: Record<TelephonyMicIssue, string> = {
  denied: 'badge.micDenied',
  'not-found': 'badge.micMissing',
  busy: 'badge.micBusy',
  unavailable: 'badge.micUnavailable',
}

const TONE_CLASSES: Record<Tone, { wrapper: string; dot: string; pulse: boolean }> = {
  live: {
    wrapper: 'bg-blue-500/10 border-blue-500/30 text-blue-300',
    dot: 'bg-blue-500',
    pulse: true,
  },
  ready: {
    // Aplat, comme les tons live/warn/off : « un dégradé n'est pas une
    // couleur » (design system §1.1) — c'était le dernier bg-gradient du badge.
    wrapper: 'bg-green-500/10 border-green-500/30 text-green-400',
    dot: 'bg-green-500',
    pulse: true,
  },
  warn: {
    wrapper: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
    dot: 'bg-amber-500',
    pulse: true,
  },
  off: {
    wrapper: 'bg-slate-700/30 border-slate-600/30 text-slate-400',
    dot: 'bg-slate-500',
    pulse: false,
  },
}

export function TelephonyStatusBadge() {
  const { t } = useTranslation('telephony')
  /**
   * SÉLECTEUR, et non `useCallState()` (mineur ④ de la review #45) : le badge
   * est monté dans le shell, donc sur TOUTE page. Branché sur l'état complet,
   * il se redessinait à chaque seconde d'un compte à rebours d'auto-dial —
   * pour afficher une pastille qui, elle, ne change pas.
   */
  const phase = useCallSelector((state) => state.phase)
  const device = useDeviceStatus()
  const { availability, toggleAvailability, requestMicrophone, tenantPaused } = useTelephony()
  const navigate = useNavigate()

  // PAUSE-PLATEAU : lu en `=== true` — les doublures historiques de
  // `useTelephony` ne posent pas la propriété.
  const paused = tenantPaused === true
  const { labelKey, tone, titleKey } = describe(phase, device, availability, paused)
  const label = t(labelKey)
  const title = t(titleKey)

  const styles = TONE_CLASSES[tone]

  /**
   * AU REPOS et poste enregistré, le badge est un BOUTON : l'agent peut se
   * déclarer indisponible sans quitter son poste. Le badge v1 le permettait ;
   * le badge v2 étant un simple `div`, l'agent PERDAIT ce contrôle dès que le
   * flag était actif — régression fonctionnelle relevée à la revue.
   *
   * Pendant un appel (ou une sonnerie, ou un wrapup), la bascule n'a pas de
   * sens : la disponibilité est dictée par l'appel en cours. Le badge redevient
   * alors un simple indicateur.
   */
  // PAUSE-PLATEAU : en pause, la disponibilité n'a plus d'objet (le serveur
  // ne fait sonner personne) — la pastille redevient un simple indicateur.
  const canToggle = phase === 'idle' && device.state === 'registered' && !paused

  const content = (
    <>
      <span className="relative flex h-2 w-2">
        {styles.pulse && (
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${styles.dot}`}
          />
        )}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${styles.dot}`} />
      </span>
      <span>{label}</span>
    </>
  )

  const base = `flex items-center gap-2 rounded-2xl border px-3 py-1.5 text-xs font-semibold shadow-lg ${styles.wrapper}`
  /**
   * Pastille-BOUTON du micro (recette #190, D4) : hors de la page Téléphone
   * c'est le SEUL déblocage, donc une vraie cible tactile. La pastille reste
   * dessinée à sa hauteur (la rangée du header ne grandit pas — §5nonies) ;
   * les 44 px sont rendus par un pseudo-élément centré, comme le `Button sm`
   * du kit. `whitespace-nowrap` + `shrink-0` : le libellé tient sur UNE ligne
   * et ne se laisse pas comprimer par ses voisins.
   */
  const micButton =
    `${base} relative shrink-0 whitespace-nowrap transition-opacity hover:opacity-80 ` +
    "before:absolute before:inset-x-0 before:top-1/2 before:h-touch before:-translate-y-1/2 before:content-['']"

  /**
   * Issue #190 — en attente du micro, la pastille EST le bouton « Activer le
   * micro » : elle est sur toute page, alors que le pré-écran ne vit que dans
   * la scène. Sans elle, un agent resté sur une autre page serait injoignable
   * sans le savoir. Le clic est le geste qui autorise la demande du navigateur.
   */
  if (phase === 'idle' && device.state === 'awaiting-mic' && !paused) {
    return (
      <button
        type="button"
        onClick={() => void requestMicrophone()}
        title={title}
        // Libellé VISIBLE court (« Activer » + pictogramme) : mesuré à 768 px,
        // « Activer le micro » en toutes lettres écrasait le fil d'ariane
        // voisin. Le nom accessible, lui, reste complet.
        aria-label={t('badge.micNeededLabel')}
        className={micButton}
      >
        {content}
        <Mic className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    )
  }

  /**
   * En PANNE micro, la pastille mène à la page Téléphone : c'est là que vit le
   * guide de sortie (refusé, absent, occupé). Sans ce lien, un agent resté sur
   * une autre page lisait « Micro refusé » sans rien pouvoir en faire.
   */
  if (phase === 'idle' && device.state === 'error' && device.error?.kind === 'mic' && !paused) {
    return (
      <button
        type="button"
        onClick={() => navigate(SCENE_ROUTE)}
        title={t('badge.micErrorTitle')}
        className={micButton}
      >
        {content}
      </button>
    )
  }

  /**
   * PAUSE-PLATEAU (recette D5) : « Plateau en pause » en toutes lettres tenait
   * sur TROIS lignes en portrait 768 px et chevauchait le fil d'ariane. Comme
   * la pastille micro (#190) : libellé VISIBLE court (« En pause »), sur une
   * ligne et non compressible ; le nom accessible et le `title` portent le
   * libellé complet. Indicateur pur (`role=status`), jamais un bouton.
   */
  if (phase === 'idle' && paused) {
    return (
      <div
        role="status"
        aria-label={t('badge.tenantPaused')}
        title={title}
        className={`${base} shrink-0 whitespace-nowrap`}
      >
        <span className="relative flex h-2 w-2">
          <span className={`relative inline-flex h-2 w-2 rounded-full ${styles.dot}`} />
        </span>
        <span>{t('badge.tenantPausedShort')}</span>
      </div>
    )
  }

  /**
   * EN LIGNE ou À QUALIFIER, la pastille MÈNE À LA SCÈNE (issue #220) : hors
   * de `/v2/softphone`, c'est — avec la barre d'appel — le chemin vers le
   * panneau d'appel et la qualification. Avant ce lot, « À qualifier » hors
   * scène ne menait nulle part : l'agent rafraîchissait, la machine repartait
   * au repos et le serveur le comptait encore 180 s en wrapup. Testé AVANT la
   * pastille micro : un wrapup prime sur l'état du poste (cf. `describe`).
   */
  if (phase === 'active' || phase === 'wrapup') {
    return (
      <button
        type="button"
        onClick={() => navigate(SCENE_ROUTE)}
        title={title}
        className={`${base} transition-opacity hover:opacity-80`}
      >
        {content}
      </button>
    )
  }

  if (!canToggle) {
    return (
      <div title={title} className={base}>
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={toggleAvailability}
      title={
        availability === null
          ? title // le titre porte déjà l'invitation à réessayer
          : t(
              availability === false
                ? 'badge.toggleToAvailable'
                : 'badge.toggleToUnavailable',
              { state: title },
            )
      }
      aria-pressed={availability === true}
      className={`${base} transition-opacity hover:opacity-80`}
    >
      {content}
    </button>
  )
}

/**
 * L'ÉTAT D'APPEL prime sur l'état du poste : pendant une conversation, savoir
 * qu'on est en ligne est plus utile que de savoir que le Device est
 * enregistré (ce qu'il est forcément).
 */
function describe(
  phase: CallPhase,
  device: ReturnType<typeof useDeviceStatus>,
  availability: boolean | null,
  tenantPaused = false,
): { labelKey: string; tone: Tone; titleKey: string } {
  switch (phase) {
    case 'incoming':
      return { labelKey: 'badge.incoming', tone: 'warn', titleKey: 'badge.incomingTitle' }
    case 'preview':
      return { labelKey: 'badge.preview', tone: 'ready', titleKey: 'badge.previewTitle' }
    case 'dialing':
      return {
        labelKey: 'badge.connecting',
        tone: 'live',
        titleKey: 'badge.connectingTitle',
      }
    case 'ringing':
      return { labelKey: 'badge.ringing', tone: 'live', titleKey: 'badge.ringingTitle' }
    case 'active':
      return { labelKey: 'badge.active', tone: 'live', titleKey: 'badge.activeTitle' }
    case 'wrapup':
      return { labelKey: 'badge.wrapup', tone: 'warn', titleKey: 'badge.wrapupTitle' }
  }

  // PAUSE-PLATEAU : au repos, la pause du plateau prime sur l'état du poste —
  // enregistré ou non, personne n'appelle ni ne reçoit. Ton `off` (clair),
  // jamais une erreur : c'est une décision du client, pas une panne. Un appel
  // EN COURS (phases ci-dessus) garde son libellé : il n'est pas coupé.
  if (tenantPaused) {
    return {
      labelKey: 'badge.tenantPaused',
      tone: 'off',
      titleKey: 'badge.tenantPausedTitle',
    }
  }

  // Au repos : c'est l'état du POSTE qui compte — et c'est précisément ce que
  // le badge v1 ne savait plus dire une fois la v2 active.
  switch (device.state) {
    case 'registered':
      // Le poste est opérationnel : reste la disponibilité DÉCLARÉE, qui peut
      // avoir été basculée à la main par l'agent (cf. bouton ci-dessus).
      if (availability === false) {
        return {
          labelKey: 'badge.unavailable',
          tone: 'off',
          titleKey: 'badge.unavailableTitle',
        }
      }
      if (availability === null) {
        // La publication a échoué : la base n'a PAS été mise à jour. Afficher
        // « Disponible » serait un mensonge — c'est précisément le défaut F-5
        // (des agents « joignables » qui ne le sont pas). On affiche le doute,
        // et le clic resynchronise.
        return {
          labelKey: 'badge.unconfirmed',
          tone: 'warn',
          titleKey: 'badge.unconfirmedTitle',
        }
      }
      return { labelKey: 'badge.available', tone: 'ready', titleKey: 'badge.availableTitle' }
    case 'initializing':
      return {
        labelKey: 'badge.initializing',
        tone: 'live',
        titleKey: 'badge.initializingTitle',
      }
    case 'awaiting-mic':
      // Issue #190 : le poste attend le clic « Activer le micro ».
      return { labelKey: 'badge.micNeeded', tone: 'warn', titleKey: 'badge.micNeededTitle' }
    case 'error':
      // Issue #190 : micro absent ou occupé ≠ micro refusé.
      if (device.error?.kind === 'mic') {
        return {
          labelKey: MIC_BADGE_KEYS[device.error.mic ?? 'unavailable'],
          tone: 'warn',
          titleKey: 'device.error.mic',
        }
      }
      return {
        labelKey: 'badge.deviceError',
        tone: 'warn',
        titleKey: device.error?.kind
          ? `device.error.${device.error.kind}`
          : 'badge.deviceErrorTitle',
      }
    case 'unregistered':
      return {
        labelKey: 'badge.disconnected',
        tone: 'off',
        titleKey: 'badge.disconnectedTitle',
      }
    default:
      return { labelKey: 'badge.down', tone: 'off', titleKey: 'badge.downTitle' }
  }
}
